import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  DollarSign,
  CreditCard,
  AlertCircle,
  Laptop,
  BarChart,
  Eye,
  ChevronRight,
  BookOpen,
  TrendingUp,
} from "lucide-react";
import { getFeeStatistics } from "../services/fees";
import { getCourses } from "../services/courses";
import { getNotices, getCourseInstructors } from "../services/dashboard";
import { FeeStatistics } from "../types/fee";
import { Course } from "../types/course";
import LoadingSpinner from "../components/common/LoadingSpinner";

interface DashboardData {
  feeStats: FeeStatistics | null;
  courses: Course[];
  notices: any[];
  instructors: any[];
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    feeStats: null,
    courses: [],
    notices: [],
    instructors: [],
  });

  // Current date
  const currentDate = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch data in parallel
        const [
          feeStatsResponse,
          coursesResponse,
          noticesResponse,
          instructorsResponse,
        ] = await Promise.allSettled([
          getFeeStatistics("statistics"),
          getCourses({ limit: 4, isActive: true }),
          getNotices(5),
          getCourseInstructors(),
        ]);

        const newData: DashboardData = {
          feeStats: null,
          courses: [],
          notices: [],
          instructors: [],
        };

        // Handle fee statistics
        if (feeStatsResponse.status === "fulfilled") {
          newData.feeStats = feeStatsResponse.value.data;
        } else {
          console.warn("Failed to fetch fee stats:", feeStatsResponse.reason);
        }

        // Handle courses
        if (coursesResponse.status === "fulfilled") {
          newData.courses = coursesResponse.value.data || [];
        } else {
          console.warn("Failed to fetch courses:", coursesResponse.reason);
        }

        // Handle notices
        if (noticesResponse.status === "fulfilled") {
          newData.notices = noticesResponse.value.data || [];
        } else {
          console.warn("Failed to fetch notices:", noticesResponse.reason);
          // Fallback notices
          newData.notices = [
            {
              title: "Prelim payment due",
              content:
                "Please ensure all preliminary examination fees are paid by the due date.",
              date: new Date().toISOString(),
            },
            {
              title: "Exam schedule",
              content:
                "Final examination schedule has been released. Check your portal for details.",
              date: new Date().toISOString(),
            },
          ];
        }

        // Handle instructors
        if (instructorsResponse.status === "fulfilled") {
          newData.instructors = instructorsResponse.value.data || [];
        } else {
          console.warn(
            "Failed to fetch instructors:",
            instructorsResponse.reason
          );
          // Fallback instructors
          newData.instructors = [
            { name: "Dr. Smith", subject: "Computer Science", avatar: "👨‍🏫" },
            {
              name: "Prof. Johnson",
              subject: "Database Systems",
              avatar: "👩‍🏫",
            },
            { name: "Dr. Williams", subject: "Programming", avatar: "👨‍💼" },
          ];
        }

        setDashboardData(newData);
      } catch (err) {
        console.error("Dashboard fetch error:", err);
        setError("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Prepare finance data from API
  const financeData = dashboardData.feeStats
    ? [
        {
          label: "Total Payable",
          amount: `$${dashboardData.feeStats.totalFees.toLocaleString()}`,
          icon: DollarSign,
          highlight: false,
        },
        {
          label: "Total Paid",
          amount: `$${dashboardData.feeStats.totalCollected.toLocaleString()}`,
          icon: CreditCard,
          highlight: true,
        },
        {
          label: "Pending",
          amount: `$${dashboardData.feeStats.totalPending.toLocaleString()}`,
          icon: AlertCircle,
          highlight: false,
        },
      ]
    : [
        {
          label: "Total Payable",
          amount: "$0",
          icon: DollarSign,
          highlight: false,
        },
        {
          label: "Total Paid",
          amount: "$0",
          icon: CreditCard,
          highlight: true,
        },
        { label: "Pending", amount: "$0", icon: AlertCircle, highlight: false },
      ];

  // Get course icon based on course name/category
  const getCourseIcon = (courseName: string) => {
    if (
      courseName.toLowerCase().includes("programming") ||
      courseName.toLowerCase().includes("software")
    ) {
      return Laptop;
    }
    if (
      courseName.toLowerCase().includes("database") ||
      courseName.toLowerCase().includes("data")
    ) {
      return BarChart;
    }
    if (
      courseName.toLowerCase().includes("math") ||
      courseName.toLowerCase().includes("statistics")
    ) {
      return TrendingUp;
    }
    return BookOpen;
  };

  if (loading) {
    return (
      <div className="bg-base-200 min-h-screen p-6 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-base-200 min-h-screen p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-error/10 border border-error/20 rounded-lg p-4">
            <p className="text-error">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 btn btn-primary btn-sm"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-50 to-blue-50/30 min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Welcome Section */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-base-content">
                Welcome back, {user?.email?.split("@")[0] || "Student"}!
              </h1>
              <p className="text-sm text-base-content/70 mt-1">
                Always stay updated in your student portal
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-base-content/70">
                {currentDate}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Finance Section */}
            <div>
              <h2 className="text-lg font-bold text-base-content mb-4">
                Finance
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {financeData.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={index}
                      className={`bg-base-100 rounded-xl shadow-lg p-4 transition-all duration-200 hover:shadow-xl ${
                        item.highlight
                          ? "ring-2 ring-primary shadow-primary/10"
                          : ""
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-base-content/70">
                            {item.label}
                          </p>
                          <p
                            className={`text-xl font-bold mt-1 ${
                              item.highlight
                                ? "text-primary"
                                : "text-base-content"
                            }`}
                          >
                            {item.amount}
                          </p>
                        </div>
                        <Icon
                          className={`w-8 h-8 ${
                            item.highlight
                              ? "text-primary"
                              : "text-base-content/50"
                          }`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Enrolled Courses */}
            <div>
              <h2 className="text-lg font-bold text-base-content mb-4">
                Enrolled Courses
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {dashboardData.courses.length > 0 ? (
                  dashboardData.courses.slice(0, 4).map((course, index) => {
                    const Icon = getCourseIcon(course.name);
                    return (
                      <div
                        key={course._id || index}
                        className="bg-base-100 rounded-xl shadow-lg p-4 transition-all duration-200 hover:shadow-xl border-0"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3">
                            <div className="bg-primary/10 rounded-lg p-2">
                              <Icon className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-base-content">
                                {course.name}
                              </h3>
                              <p className="text-sm text-base-content/70 mt-1">
                                {course.description || "Course description"}
                              </p>
                            </div>
                          </div>
                          <button className="btn btn-primary btn-sm rounded-full">
                            <Eye className="w-4 h-4" />
                            View
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="col-span-2 text-center py-8 text-base-content/70">
                    <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No courses enrolled yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Panel */}
          <div className="space-y-6">
            {/* Course Instructors */}
            <div className="bg-base-100 rounded-xl shadow-lg p-4 transition-all duration-200 hover:shadow-xl border-0">
              <h3 className="text-sm font-semibold text-base-content mb-4">
                Course instructors
              </h3>
              <div className="flex -space-x-2">
                {dashboardData.instructors
                  .slice(0, 3)
                  .map((instructor, index) => (
                    <div
                      key={index}
                      className="w-10 h-10 rounded-full ring ring-primary ring-offset-base-100 ring-offset-2 bg-base-200 flex items-center justify-center text-lg"
                      title={instructor.name}
                    >
                      {instructor.avatar || "👤"}
                    </div>
                  ))}
              </div>
            </div>

            {/* Daily Notice */}
            <div className="bg-base-100 rounded-xl shadow-lg p-4 transition-all duration-200 hover:shadow-xl border-0">
              <h3 className="text-sm font-semibold text-base-content mb-4">
                Daily notice
              </h3>
              <div className="space-y-4">
                {dashboardData.notices.slice(0, 2).map((notice, index) => (
                  <div
                    key={index}
                    className="pb-4 last:pb-0 border-0 shadow-sm rounded-lg p-3 bg-slate-50/50"
                  >
                    <h4 className="font-semibold text-base-content text-sm mb-2">
                      {notice.title}
                    </h4>
                    <p className="text-xs text-base-content/70 mb-3">
                      {notice.content}
                    </p>
                    <button className="flex items-center text-primary text-xs font-semibold hover:underline">
                      See more
                      <ChevronRight className="w-3 h-3 ml-1" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Additional Statistics */}
        {dashboardData.feeStats && (
          <div className="mt-8">
            <div className="bg-base-100 rounded-xl shadow-lg p-6 transition-all duration-200 hover:shadow-xl border-0">
              <h3 className="text-lg font-bold text-base-content mb-4">
                Statistics
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary">
                    {dashboardData.feeStats.collectionRate.toFixed(1)}%
                  </p>
                  <p className="text-sm text-base-content/70">
                    Collection Rate
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-base-content">
                    $
                    {dashboardData.feeStats.averageFeePerStudent.toLocaleString()}
                  </p>
                  <p className="text-sm text-base-content/70">
                    Avg Fee/Student
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-warning">
                    ${dashboardData.feeStats.totalOverdue.toLocaleString()}
                  </p>
                  <p className="text-sm text-base-content/70">Overdue Amount</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-info">
                    {dashboardData.courses.length}
                  </p>
                  <p className="text-sm text-base-content/70">
                    Available Courses
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
